import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Ruler,
  ShieldCheck,
  Building2,
  Layers,
  CheckCircle2,
  Phone,
  MessageCircle,
} from "lucide-react";

import { FadeUp, SectionLabel } from "../components/Motion";
import { getProjects } from "../services/projectsApi";
import { mapApiProject } from "../utils/projectMapper";
import { buildWhatsappUrl, PHONE_TEL } from "../lib/whatsapp";

export default function ProjectDetail({ onEnquire }) {
  const { slug } = useParams();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadProject() {
      try {
        setLoading(true);
        setError("");

        const apiProjects = await getProjects();
        const mappedProjects = apiProjects.map(mapApiProject);

        const selectedProject = mappedProjects.find(
          (item) => item.slug === slug
        );

        if (!selectedProject) {
          setError("Project not found.");
          return;
        }

        setProject(selectedProject);
      } catch (err) {
        console.error("Project detail loading error:", err);
        setError("Unable to load this project.");
      } finally {
        setLoading(false);
      }
    }

    loadProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <p className="text-[#555] text-lg">
          Loading project...
        </p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-[#FAFAFA] px-6">
        <p className="text-[#555] text-lg">
          {error || "Project not found."}
        </p>

        <Link
          to="/projects"
          className="brand-gradient text-white rounded-full px-7 py-3"
        >
          Back to Projects
        </Link>
      </div>
    );
  }

  const waMsg =
    `Hello, I'm interested in the project ${project.name}.` +
    `\n\nName: \nPhone: \nMessage: `;

  const waUrl = buildWhatsappUrl(waMsg);

  const highlights = [
    {
      icon: Building2,
      label: "Project Type",
      value: project.category || "—",
    },
    {
      icon: Layers,
      label: "Configuration",
      value: project.config || "—",
    },
    {
      icon: Ruler,
      label: "Size",
      value: project.size || "—",
    },
    {
      icon: MapPin,
      label: "Location",
      value: project.locationShort || "—",
    },
    {
      icon: CheckCircle2,
      label: "Status",
      value: project.status || "—",
    },
    {
      icon: ShieldCheck,
      label: "Approval",
      value:
        project.approvals?.join(", ") ||
        project.approval ||
        "—",
    },
  ];

  const mapSrc =
    `https://www.google.com/maps?q=${encodeURIComponent(
      project.location
    )}&output=embed`;

  const handleEnquire = () => {
    if (typeof onEnquire === "function") {
      onEnquire({
        projectName: project.name,
      });
    }
  };

  return (
    <div
      data-testid="project-detail-page"
      className="bg-[#FAFAFA] pb-28 md:pb-32"
    >
      {/* HERO */}
      <section className="relative min-h-[80vh] md:min-h-[90vh] w-full overflow-hidden">
        <div className="absolute inset-0">
          {project.cover ? (
            <img
              src={project.cover}
              alt={project.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-300" />
          )}

          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80" />
        </div>

        <div className="relative z-10 min-h-[80vh] md:min-h-[90vh] flex flex-col justify-end container mx-auto px-6 md:px-12 max-w-7xl pb-16 md:pb-20 pt-28">
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6 md:mb-10 self-start"
            data-testid="back-to-projects"
          >
            <ArrowLeft className="h-4 w-4" />
            All projects
          </Link>

          <FadeUp>
            <span className="inline-block bg-white/15 backdrop-blur border border-white/25 text-white text-[10px] font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full">
              {project.category}
            </span>

            <h1 className="font-serif text-white text-5xl sm:text-6xl md:text-7xl mt-6 leading-[1.02] tracking-tight max-w-4xl">
              {project.name}
            </h1>

            <p className="mt-5 text-white/90 font-serif italic text-2xl md:text-3xl">
              {project.config}
            </p>

            <p className="mt-3 text-white/85 text-base md:text-lg flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {project.location}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              {project.statusKey !== "completed" && (
                <button
                  type="button"
                  onClick={handleEnquire}
                  data-testid="hero-enquire-btn"
                  className="brand-gradient text-white rounded-full px-8 py-4 font-medium hover:-translate-y-0.5 hover:shadow-xl transition-all"
                >
                  Enquire Now
                </button>
              )}

              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 border border-white/70 text-white rounded-full px-8 py-4 font-medium hover:bg-white hover:text-[#1A1A1A] transition-all"
              >
                <Phone className="h-4 w-4" />
                Call
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          <FadeUp>
            <SectionLabel>Overview</SectionLabel>

            <p className="mt-6 font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.25] tracking-tight text-[#1A1A1A]">
              {project.description ||
                `${project.name} is a ${project.configuration} project located in ${project.location}.`}
            </p>
          </FadeUp>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="py-20 md:py-28 bg-white border-y border-black/5">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl">
          <FadeUp className="max-w-2xl">
            <SectionLabel>Highlights</SectionLabel>

            <h2 className="font-serif text-4xl md:text-5xl mt-5 tracking-tight text-[#1A1A1A]">
              Key details at a glance.
            </h2>
          </FadeUp>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {highlights.map((highlight, index) => {
              const Icon = highlight.icon;

              return (
                <FadeUp
                  key={highlight.label}
                  delay={(index % 2) * 0.06}
                >
                  <div
                    data-testid={`highlight-${index}`}
                    className="flex items-start gap-5 p-6 md:p-7 bg-[#FAFAFA] border border-black/5 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300"
                    style={{ borderRadius: "20px" }}
                  >
                    <div className="h-12 w-12 shrink-0 rounded-full brand-gradient flex items-center justify-center text-white">
                      <Icon className="h-5 w-5" />
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#555]">
                        {highlight.label}
                      </p>

                      <p className="mt-1 text-[#1A1A1A] text-xl font-medium">
                        {highlight.value}
                      </p>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp className="max-w-2xl">
            <SectionLabel>Gallery</SectionLabel>

            <h2 className="font-serif text-4xl md:text-5xl mt-5 tracking-tight text-[#1A1A1A]">
              Inside the project.
            </h2>
          </FadeUp>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {project.gallery && project.gallery.length > 0 ? (
              project.gallery.map((src, index) => (
                <FadeUp
                  key={`${src}-${index}`}
                  delay={(index % 3) * 0.05}
                >
                  <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`gallery-item-${index}`}
                    className="block overflow-hidden group"
                    style={{ borderRadius: "20px" }}
                  >
                    <img
                      src={src}
                      alt={`${project.name} ${index + 1}`}
                      loading="lazy"
                      className="w-full h-80 md:h-96 object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </a>
                </FadeUp>
              ))
            ) : (
              <p className="text-[#555] col-span-full text-center py-12">
                No gallery images available.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="py-20 md:py-28 bg-[#F5F2F0]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <FadeUp className="max-w-2xl">
            <SectionLabel>Location</SectionLabel>

            <h2 className="font-serif text-4xl md:text-5xl mt-5 tracking-tight text-[#1A1A1A]">
              Find us here.
            </h2>

            <p
              className="mt-4 text-[#555] text-lg"
              data-testid="project-address"
            >
              {project.location}
            </p>
          </FadeUp>

          <FadeUp className="mt-10">
            <div
              className="overflow-hidden border border-black/5 shadow-sm"
              style={{ borderRadius: "20px" }}
            >
              <iframe
                title={`${project.name} location`}
                data-testid="project-map"
                src={mapSrc}
                width="100%"
                height="450"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </FadeUp>

          {project.mapUrl && (
            <div className="mt-6 text-center">
              <a
                href={project.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#7A2E5D] font-medium hover:underline"
              >
                <MapPin className="h-4 w-4" />
                Open in Google Maps
              </a>
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 brand-gradient text-white">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center">
          <FadeUp>
            <h2 className="font-serif text-5xl md:text-6xl leading-tight tracking-tight">
              Interested in this{" "}
              <span className="italic font-light">
                project?
              </span>
            </h2>

            <p className="mt-6 text-white/85 text-lg md:text-xl max-w-xl mx-auto">
              Our team will walk you through availability, pricing and next
              steps.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-btn"
                className="inline-flex items-center gap-2 bg-white text-[#7A2E5D] rounded-full px-8 py-4 font-medium text-base hover:-translate-y-0.5 hover:shadow-xl transition-all"
              >
                <MessageCircle className="h-4 w-4" />
                Enquire on WhatsApp
              </a>

              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="cta-call-btn"
                className="inline-flex items-center gap-2 border border-white/70 text-white rounded-full px-8 py-4 font-medium text-base hover:bg-white hover:text-[#7A2E5D] transition-all"
              >
                <Phone className="h-4 w-4" />
                Call Now
              </a>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* STICKY MOBILE CTA */}
      <div
        data-testid="mobile-sticky-cta"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-black/10 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex gap-3"
      >
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="mobile-cta-whatsapp"
          className="flex-1 brand-gradient text-white rounded-full py-3 font-medium text-sm text-center inline-flex items-center justify-center gap-2"
        >
          <MessageCircle className="h-4 w-4" />
          WhatsApp
        </a>

        <a
          href={`tel:${PHONE_TEL}`}
          data-testid="mobile-cta-call"
          className="flex-1 border-2 border-[#7A2E5D] text-[#7A2E5D] rounded-full py-3 font-medium text-sm text-center inline-flex items-center justify-center gap-2"
        >
          <Phone className="h-4 w-4" />
          Call
        </a>
      </div>
    </div>
  );
}
